import React, { useState } from "react";
import styles from "./CSS/VideoContent.module.css";
import { Edit, Trash, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useVideos, useUpdateVideo, useDeleteVideo } from "../../../hooks/admin/useAdminVideoUpload";

// Separate component for the Edit icon
const EditIconComponent = ({ id, onEdit }) => {
  return (
    <Edit
      className={styles.icon}
      stroke="black"
      onClick={() => onEdit(id)}
      size={18}
    />
  );
};


// Separate component for the Delete icon
const DeleteIconComponent = ({ id, onDelete }) => {
  return (
    <Trash
      className={styles.icon}
      stroke="black"
      onClick={() => onDelete(id)}
      size={18}
    />
  );
};

// Play Button Component
const PlayButtonComponent = () => {
  return (
    <div className={styles.playButton}>
      <Play color="#fff" size={24} />
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ message, onConfirm, onCancel }) => {
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <p>{message}</p>
        <div className={styles.modalButtons}>
          <button className={styles.modalButton} onClick={onConfirm}>
            Confirm
          </button>
          <button className={styles.modalButton} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const VideoContent = () => {
  // React Query hooks
  const { videos, isLoading,} = useVideos();
  const { updateVideo } = useUpdateVideo();
  const { deleteVideo, isLoading: isDeleting } = useDeleteVideo();
  
  // Local state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [showModal, setShowModal] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const navigate = useNavigate();

  const handleEdit = (id) => {
    console.log("Edit video with ID:", id);
    navigate(`/admin/edit-video/${id}`); 
  };

  const handleHide = (id) => {
    const videoToToggle = videos.find(video => video.id === id);
    if (videoToToggle) {
      updateVideo({
        id,
        data: { 
          ...videoToToggle, 
          is_public: !videoToToggle.is_public,
          is_active: videoToToggle.is_active 
        }
      });
    }
  };

  // Opens the confirmation modal before deleting
  const confirmDelete = (id) => {
    setVideoToDelete(id);
    setShowModal(true);
  };

  // Called when the user confirms deletion
  const confirmDeleteAction = () => {
    if (videoToDelete !== null) {
      deleteVideo(videoToDelete);
      setVideoToDelete(null);
      setShowModal(false);
    }
  };

  const cancelDelete = () => {
    setVideoToDelete(null);
    setShowModal(false);
  };

  // Display loading state
  if (isLoading) {
    return <div className={styles.loadingContainer}>Loading videos...</div>;
  }

  // Handle case when videos is undefined
  const videosList = videos || [];

  // Pagination logic
  const indexOfLastVideo = currentPage * itemsPerPage;
  const indexOfFirstVideo = indexOfLastVideo - itemsPerPage;
  const currentVideos = videosList.slice(indexOfFirstVideo, indexOfLastVideo);
  const totalPages = Math.ceil(videosList.length / itemsPerPage);

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const getAppropriateImageUrl = (video) => {
    // Get the most appropriate image URL based on what's available
    return video.computer_image_url || video.mobile_image_url;
  };

  return (
    <div>
      <div className={styles.aboveHeader}>
        <h1>Video Library</h1>
        <div>
          <button 
            className={styles.addVideoButton} 
            onClick={() => navigate('/admin/add-videos')}
          >
            Add Videos
          </button>
        </div>
      </div>
      
      {videosList.length === 0 ? (
        <div className={styles.noVideosMessage}>
          No videos found. Add some videos to get started.
        </div>
      ) : (
        <>
          <div className={styles.videosContainer}>
            {currentVideos.map((video) => (
              <div key={video.id} className={styles.videoItem}>
                <img
                  src={getAppropriateImageUrl(video)}
                  alt={video.title}
                  className={styles.videoImage}
                />
                <PlayButtonComponent />
                <div className={styles.iconsOverlay}>
                  <EditIconComponent id={video.id} onEdit={handleEdit} />
                  <DeleteIconComponent id={video.id} onDelete={confirmDelete} />
                </div>
                {!video.is_public && (
                  <div className={styles.hiddenBadge}>Hidden</div>
                )}
                {!video.is_active && (
                  <div className={`${styles.hiddenBadge} ${styles.inactiveBadge}`}>Inactive</div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.paginationButton}
                onClick={goToPrevPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className={styles.pageInfo}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className={styles.paginationButton}
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Confirmation Modal */}
      {showModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this video?"
          onConfirm={confirmDeleteAction}
          onCancel={cancelDelete}
        />
      )}
      
      {/* Loading overlay for delete operation */}
      {isDeleting && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner}></div>
        </div>
      )}
    </div>
  );
};

export default VideoContent;